import { useParams } from 'react-router-dom';
import { CompanyLinks } from './types';
import CompanyDashboard from './components/CompanyDashboard';
import CompanyProfile from './components/CompanyProfile';
import CompanyPlan from './components/CompanyPlan';
import { Company } from '@/context/types';
import {
  useGetBuildsByQueryQuery,
  useGetPlanByCompanyIdQuery,
  useGetUsersByQueryQuery,
} from '@/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetDashboardDataQuery } from '@/store/slices/companySlice';
import CompanySkeleton from './components/CompanySkeleton';
import { Column, EmptyDiv } from '@/components/SettingsLayout';
import { Text, Icon, IconType } from '@/ui';
import { useTranslation } from 'react-i18next';
import { BuildPaginationFilter, TypeOfBuild } from '@/types/build.type';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';
import { useGetUser } from '@/hooks';
import { UserTypes } from '@/enums';
import CompanyPricing from './components/CompanyPricing';

type Props = {
  company: Company;
};
function SingleCompanyContent({ company }: Props) {
  const { t } = useTranslation();
  const { id, info } = useParams();
  const { currentUser } = useGetUser();
  const {
    data,
    isLoading: userLoader,
    isError,
  } = useGetUsersByQueryQuery(
    id && currentUser?.companyId
      ? {
          organizationId: currentUser.companyId,
          companyId: id,
          query: {
            type: UserTypes.OWNER,
            companies: [id],
          },
        }
      : skipToken,
  );

  const { data: singlePlan, isLoading: planLoading } = useGetPlanByCompanyIdQuery(
    id && currentUser?.companyId
      ? { companyId: id, organizationId: currentUser.companyId }
      : skipToken,
  );

  const { data: dashboardData, isLoading } = useGetDashboardDataQuery(
    company?._id && currentUser?.companyId
      ? {
          id: company._id.toString(),
          organizationId: currentUser.companyId,
        }
      : skipToken,
  );
  const { filter } = usePaginationFilter<BuildPaginationFilter>({
    limit: 25,
    offset: 0,
    search: '',
    buildType: TypeOfBuild.ORG_THEME,
  });

  const { data: buildsData, isLoading: buildsLoading } = useGetBuildsByQueryQuery(
    company?.organizationId
      ? {
          companyId: id || '',
          organizationId: company.organizationId,
          filters: filter,
        }
      : skipToken,
  );

  if (userLoader || isLoading || planLoading || buildsLoading) {
    return <CompanySkeleton />;
  }

  // Only a real fetch failure should block the page. A company with no owner user
  // (empty `data`) is a valid state — the dashboard doesn't depend on the owner.
  if (isError) {
    return (
      <Column gapSize='32'>
        <EmptyDiv>
          <Icon type={IconType.Alert} width={48} height={48} fill='transparent' />
          <Text size='m' weight='semi-bold'>
            {t('Errors.general')}
          </Text>
          <Text size='s' weight='light-medium'>
            {t('Companies.no_companies')}
          </Text>
        </EmptyDiv>
      </Column>
    );
  }

  const companyOwner = data?.[0];

  return (
    <>
      {info === CompanyLinks.DASHBOARD && (
        <CompanyDashboard
          company={company}
          dashboardData={dashboardData}
          companyOwner={companyOwner}
          builds={buildsData?.data || []}
          plan={singlePlan}
        />
      )}
      {info === CompanyLinks.PROFILE &&
        (companyOwner ? (
          <CompanyProfile companyOwner={companyOwner} company={company} />
        ) : (
          <Column gapSize='32'>
            <EmptyDiv>
              <Icon type={IconType.Alert} width={48} height={48} fill='transparent' />
              <Text size='m' weight='semi-bold'>
                {t('Errors.general')}
              </Text>
              <Text size='s' weight='light-medium'>
                {t('Companies.no_companies')}
              </Text>
            </EmptyDiv>
          </Column>
        ))}
      {info === CompanyLinks.PLAN && singlePlan && (
        <CompanyPlan plan={singlePlan} company={company} />
      )}
      {info === CompanyLinks.PRICING && singlePlan && (
        <CompanyPricing company={company} plan={singlePlan} />
      )}
    </>
  );
}

export default SingleCompanyContent;
